import React, { useCallback, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  ArrowLeft,
  BarChart3,
  Home,
  Lock,
  Plug,
  PlugZap,
  RotateCcw,
  Sparkles,
  Volume2,
  VolumeX,
} from "lucide-react";
import { audioSupported, setMuted } from "../lib/synthLab";
import { seededRng, makeExercise, conceptLabel, RUNG_BY_ID } from "../lib/synthLadder";
import {
  buildSession,
  recordResult,
  rungStars,
  unlockedConcepts,
  keyOf,
} from "../lib/synthMastery";
import { loadMastery, saveMastery } from "../components/synthlab/masteryStore";
import { loadCcMap, MIDI_SUPPORTED } from "../components/synthlab/useSynthInput";
import LabShell from "../components/synthlab/LabShell";
import Stars from "../components/synthlab/Stars";
import {
  HearRound,
  NameRound,
  WayRound,
  DialRound,
  FixRound,
} from "../components/synthlab/PracticeRounds";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { cn } from "../lib/utils";

const CAN_PLAY = audioSupported();

const ROUND_COMPONENTS = {
  hear: HearRound,
  name: NameRound,
  way: WayRound,
  dial: DialRound,
  fix: FixRound,
};

// Build one session's worth of concrete exercises from the current mastery.
// Seeded per start so "Go again" gives a fresh run, deterministic within it.
const makeRun = (mastery, focus) => {
  const seed = `${focus || "mix"}:${Date.now()}`;
  const session = buildSession(mastery, { seed, focus });
  const rng = seededRng(`${seed}:ex`);
  // Only taught concepts may be named in rounds; a lesson in THIS session
  // counts as taught, since its reps come right after it.
  const pool = [...new Set([...unlockedConcepts(mastery), ...session.filter((d) => d.rung === "hear").map((d) => d.concept)])];
  return {
    key: seed,
    rounds: session.map((d) => makeExercise(d, { rng, unlocked: pool })),
  };
};

const SynthLabPractice = () => {
  const [params] = useSearchParams();
  const focus = params.get("focus");

  const [mastery, setMastery] = useState(loadMastery);
  const [run, setRun] = useState(() => makeRun(loadMastery(), focus));
  const [idx, setIdx] = useState(0);
  const [results, setResults] = useState([]);
  const [muted, setMutedState] = useState(false);

  // MIDI-as-answer: opt-in per visit, only offered when knobs are bound.
  const [ccMap] = useState(loadCcMap);
  const [midiEnabled, setMidiEnabled] = useState(false);
  const midiAvailable = MIDI_SUPPORTED && CAN_PLAY && Object.keys(ccMap).length > 0;

  const toggleMute = () => {
    setMutedState((m) => {
      setMuted(!m);
      return !m;
    });
  };

  const ex = run.rounds[idx] || null;
  const total = run.rounds.length;
  const finished = total > 0 && idx >= total;

  const resolve = useCallback(
    (correct) => {
      const round = run.rounds[idx];
      if (!round) return;
      setResults((r) => [...r, { concept: round.concept, rung: round.kind, correct }]);
      setMastery((m) => {
        const next = recordResult(m, round.concept, round.kind, correct);
        saveMastery(next);
        return next;
      });
    },
    [run, idx]
  );

  const next = useCallback(() => setIdx((i) => i + 1), []);

  const goAgain = () => {
    setRun(makeRun(mastery, focus));
    setIdx(0);
    setResults([]);
  };

  const correctCount = results.filter((r) => r.correct).length;
  const gradedResults = results.filter((r) => r.rung !== "hear");

  // Unique rungs this session touched, with their post-session stars.
  const touched = [];
  results.forEach((r) => {
    if (r.rung === "hear") return;
    const k = keyOf(r.concept, r.rung);
    if (!touched.some((t) => t.k === k)) {
      touched.push({ k, concept: r.concept, rung: r.rung });
    }
  });

  const RoundComponent = ex ? ROUND_COMPONENTS[ex.kind] : null;

  return (
    <LabShell
      narrow
      eyebrow={focus ? "targeted practice" : "practice session"}
      title={focus ? `Practice: ${conceptLabel(focus)}` : "Practice"}
      actions={
        <>
          {midiAvailable && (
            <Button
              variant={midiEnabled ? "default" : "outline"}
              size="sm"
              onClick={() => setMidiEnabled((m) => !m)}
              title="Answer Dial/Fix rounds by turning your bound hardware knobs"
            >
              {midiEnabled ? <PlugZap className="h-4 w-4" /> : <Plug className="h-4 w-4" />}
              MIDI
            </Button>
          )}
          {CAN_PLAY && (
            <Button variant="outline" size="sm" onClick={toggleMute}>
              {muted ? <VolumeX className="h-4 w-4" /> : <Volume2 className="h-4 w-4" />}
            </Button>
          )}
          <Button asChild variant="ghost" size="sm">
            <Link to="/synth-lab">
              <ArrowLeft className="h-4 w-4" />
              Exit
            </Link>
          </Button>
        </>
      }
    >
      {!CAN_PLAY && (
        <p className="mt-4 rounded-lg border border-amber-300 bg-amber-50 px-3 py-2 text-sm text-amber-800">
          Your browser doesn't support Web Audio, so ear-training rounds can't
          play — try a current Chrome, Edge, Firefox or Safari.
        </p>
      )}

      {total === 0 ? (
        <Card className="mt-6 bg-card/80 backdrop-blur">
          <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
            <Lock className="h-6 w-6 text-muted-foreground" />
            <p className="text-sm text-muted-foreground">
              {focus
                ? `${conceptLabel(focus)} is still locked — earn a star on the concept before it first.`
                : "Nothing to practice yet."}
            </p>
            <Button asChild size="sm">
              <Link to="/synth-lab">
                <Home className="h-4 w-4" />
                Back to the journey
              </Link>
            </Button>
          </CardContent>
        </Card>
      ) : finished ? (
        /* ---- session summary ---- */
        <Card className="mt-6 bg-card/80 backdrop-blur">
          <CardContent className="py-8 text-center">
            <p className="text-sm text-muted-foreground">Session complete</p>
            <p className="mt-1 text-4xl font-bold">
              <span
                className={cn(
                  gradedResults.length &&
                    correctCount / Math.max(1, gradedResults.length) >= 0.75
                    ? "text-emerald-600"
                    : "text-amber-600"
                )}
              >
                {results.filter((r) => r.correct && r.rung !== "hear").length}
              </span>
              <span className="text-muted-foreground">/{gradedResults.length || results.length}</span>
            </p>
            {touched.length > 0 && (
              <div className="mx-auto mt-5 grid max-w-sm gap-2 text-left">
                {touched.map((t) => (
                  <div
                    key={t.k}
                    className="flex items-center justify-between rounded-lg border border-border bg-card/60 px-3 py-2 text-sm"
                  >
                    <span>
                      {conceptLabel(t.concept)}
                      <span className="text-muted-foreground"> · {RUNG_BY_ID[t.rung].label}</span>
                    </span>
                    <Stars n={rungStars(mastery, t.concept, t.rung)} />
                  </div>
                ))}
              </div>
            )}
            <div className="mt-6 flex flex-wrap items-center justify-center gap-2">
              <Button size="sm" onClick={goAgain}>
                <RotateCcw className="h-4 w-4" />
                Go again
              </Button>
              <Button asChild size="sm" variant="outline">
                <Link to="/synth-lab">
                  <BarChart3 className="h-4 w-4" />
                  Journey
                </Link>
              </Button>
              <Button asChild size="sm" variant="ghost">
                <Link to="/synth-lab/playground">
                  <Sparkles className="h-4 w-4" />
                  Playground
                </Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      ) : (
        /* ---- an active round ---- */
        <>
          <div className="mt-6 flex items-center justify-between text-xs text-muted-foreground">
            <span>
              Round {idx + 1} of {total}
            </span>
            <span>{results.filter((r) => r.correct && r.rung !== "hear").length} correct</span>
          </div>
          <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
            <div
              className="h-full rounded-full bg-primary transition-all"
              style={{ width: `${(idx / total) * 100}%` }}
            />
          </div>
          <Card className="mt-4 bg-card/80 backdrop-blur">
            <CardContent className="pt-6">
              <RoundComponent
                key={`${run.key}:${idx}`}
                ex={ex}
                midiEnabled={midiEnabled}
                ccMap={ccMap}
                onResolve={resolve}
                onNext={next}
                nextLabel={idx + 1 >= total ? "Finish" : "Next"}
              />
            </CardContent>
          </Card>
        </>
      )}
    </LabShell>
  );
};

export default SynthLabPractice;
